/* Sandbox (ADR 0066, bigdayz.new): play a Sandbox Fan in a Sandbox Chat on this
   Account and watch the real AI answer with the Account's live setup. Nothing reaches
   the Platform; every AI reply is still a real, billed Turn.

   Layout follows the approved compact design: the test fan on the left, the chat in
   the middle, the Account's Sandbox Chats on the right. */

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../../../api/client';
import type { Account, SandboxChatSummary, SandboxMessage, SandboxPacing } from '../../../../api/client';
import { useAuth } from '../../../../context/AuthContext';
import { LoadingBlock } from '../../../Spinner';
import { Btn, ErrorLine, Icon, InfoLine, Seg, Toggle } from '../kit';
import { font as f } from '../style';
import { C } from '../tokens';
import '../compact.css';
import { ChatPane } from './ChatPane';
import { ListPicker } from './ListPicker';
import { NewChatDialog } from './NewChatDialog';
import { TraceDrawer } from './TraceDrawer';
import { MAX_OPEN_CHATS, money, offersOf, replyState, rowsOf, spentOf, whenLabel } from './model';
import { useSandboxChat } from './useSandboxChat';

const PACINGS: SandboxPacing[] = ['account', 'max_speed'];
let tmpSeq = 0;

export function SandboxPage({ account }: { account: Account }) {
  const { t } = useTranslation('assistantCompact');
  const accountId = account.account_id;

  if (account.platform === 'fansly') {
    return (
      <Frame>
        <Centered icon="flask" title={t('sb.title')} body={t('sb.fansly')} />
      </Frame>
    );
  }
  return <Sandbox key={accountId} accountId={accountId} />;
}

function Sandbox({ accountId }: { accountId: string }) {
  const { t } = useTranslation('assistantCompact');
  const { user, isSuperAdmin } = useAuth();
  const [params, setParams] = useSearchParams();
  const fanId = params.get('chat');

  const [chats, setChats] = useState<SandboxChatSummary[] | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  const [ofLists, setOfLists] = useState<string[]>([]);
  const [creating, setCreating] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [pacing, setPacing] = useState<SandboxPacing>('account');
  const [showSystem, setShowSystem] = useState(true);
  const [unlocking, setUnlocking] = useState<Set<number>>(new Set());
  const [traceTurn, setTraceTurn] = useState<string | null>(null);

  const chat = useSandboxChat(accountId, fanId);
  const { meta, messages, setMsgs, setMeta, kick } = chat;

  const select = useCallback((id: string | null) => {
    setParams((cur) => {
      const next = new URLSearchParams(cur);
      if (id) next.set('chat', id); else next.delete('chat');
      return next;
    }, { replace: true });
  }, [setParams]);

  const loadChats = useCallback(async () => {
    try {
      const r = await api.listSandboxChats(accountId);
      setChats(r.chats);
      setListError(null);
      return r.chats;
    } catch (e) {
      setListError(e instanceof Error ? e.message : String(e));
      setChats((c) => c ?? []);
      return null;
    }
  }, [accountId]);

  useEffect(() => {
    void loadChats().then((list) => {
      // Open the newest chat when none is picked (or the picked one is gone).
      if (list && list.length && !list.some((c) => c.fan_id === fanId)) select(list[list.length - 1]!.fan_id);
    });
    api.getAccountOfLists(accountId).then((ls) => setOfLists(ls.map((l) => l.of_list_name).filter(Boolean)), () => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId]);
  const refreshLists = async () => {
    await api.refreshListsAndWait(accountId);
    setOfLists((await api.getAccountOfLists(accountId)).map((l) => l.of_list_name).filter(Boolean));
  };

  // A chat opens in the pacing it was last used with.
  const pacedFor = useRef<string | null>(null);
  useEffect(() => {
    if (meta && pacedFor.current !== meta.fan_id) {
      pacedFor.current = meta.fan_id;
      setPacing(meta.pacing === 'max_speed' ? 'max_speed' : 'account');
    }
  }, [meta]);
  useEffect(() => { setActionError(null); setUnlocking(new Set()); setTraceTurn(null); }, [fanId]);

  const summary = chats?.find((c) => c.fan_id === fanId) ?? null;
  const profile = meta?.profile ?? summary?.profile ?? {};
  const fanName = profile.name || profile.displayName || 'Test fan';
  const createdBy = meta?.created_by ?? summary?.created_by;
  const deletable = isSuperAdmin || (!!user && createdBy === user.id);
  const rows = useMemo(() => rowsOf(messages, showSystem), [messages, showSystem]);
  // Say plainly that replies cost Turns until the operator has sent in this chat.
  const [sentIn, setSentIn] = useState<Set<string>>(new Set());
  const atLimit = (chats?.length ?? 0) >= MAX_OPEN_CHATS;

  const fail = (e: unknown) => setActionError(e instanceof Error ? e.message : String(e));

  /** Show the fan's action at once; swap in the brain's id when it accepts it. */
  const optimistic = async (row: Partial<SandboxMessage>, call: () => Promise<{ message_id: number }>) => {
    const tmp = -(1e12 + (tmpSeq += 1));
    setMsgs((cur) => [...cur, { id: tmp, sender: 'fan', created_at: new Date().toISOString(), ...row } as SandboxMessage]);
    setActionError(null);
    try {
      const r = await call();
      setMsgs((cur) => cur.map((m) => (m.id === tmp ? { ...m, id: -r.message_id } : m)));
      if (fanId) setSentIn((s) => new Set(s).add(fanId));
      kick();
      return true;
    } catch (e) {
      setMsgs((cur) => cur.filter((m) => m.id !== tmp));
      fail(e);
      return false;
    }
  };

  const send = (text: string) => (fanId ? optimistic({ text }, () => api.sendSandboxFanMessage(accountId, fanId, text, pacing)) : Promise.resolve(false));
  const tip = (amount: number, text: string) => (fanId ? optimistic({ text, tip_amount: amount }, () => api.sendSandboxTip(accountId, fanId, amount, text)) : Promise.resolve(false));

  const unlock = async (messageId: number) => {
    if (!fanId) return;
    setUnlocking((s) => new Set(s).add(messageId));
    setActionError(null);
    try {
      await api.unlockSandboxPpv(accountId, fanId, messageId);
      setMsgs((cur) => cur.map((m) => (m.id === messageId ? { ...m, is_purchased: true } : m)));
      kick(true);
    } catch (e) {
      fail(e);
    } finally {
      setUnlocking((s) => { const n = new Set(s); n.delete(messageId); return n; });
    }
  };

  const remove = async () => {
    if (!fanId) return;
    try {
      await api.deleteSandboxChat(accountId, fanId);
      const list = await loadChats();
      select(list && list.length ? list[list.length - 1]!.fan_id : null);
    } catch (e) {
      fail(e);
    }
  };

  const saveLists = async (names: string[]) => {
    if (!fanId) return false;
    try {
      const r = await api.setSandboxLists(accountId, fanId, names);
      setMeta((m) => (m ? { ...m, lists: r.lists } : m));
      void loadChats();
      return true;
    } catch (e) {
      fail(e);
      return false;
    }
  };

  const created = async (id: string) => {
    setCreating(false);
    await loadChats();
    select(id);
  };

  const offers = offersOf(messages);
  const waiting = replyState(messages);
  const aiState = meta?.turn_in_progress ? 'replying'
    : waiting === 'queued' ? 'queued'
      : meta?.follow_up_pending ? 'followUp'
        : waiting === 'unanswered' ? 'unanswered' : 'idle';

  let center: ReactNode;
  if (chats === null) center = <LoadingBlock />;
  else if (listError && !chats.length) center = <Centered icon="alert-circle" title={t('sb.title')} body={t('sb.unavailable', { msg: listError })} />;
  else if (!fanId) {
    center = chats.length
      ? <Centered icon="message-circle" title={t('sb.title')} body={t('sb.pick')} />
      : <Centered icon="flask" title={t('sb.empty.title')} body={t('sb.empty.body')} action={<Btn kind="primary" icon="plus" onClick={() => setCreating(true)}>{t('sb.newChat')}</Btn>} />;
  } else if (chat.missing) center = <Centered icon="alert-circle" title={t('sb.title')} body={t('sb.gone')} />;
  else if (chat.loading && !meta) center = <LoadingBlock />;
  else {
    center = (
      <ChatPane
        name={fanName}
        status={aiState === 'replying' ? 'typing' : aiState === 'queued' ? 'received' : 'online'}
        rows={rows}
        showCost={!!fanId && !sentIn.has(fanId)}
        unlocking={unlocking}
        deletable={deletable}
        onSend={send}
        onTip={tip}
        onUnlock={(id) => void unlock(id)}
        onDelete={() => void remove()}
        onTrace={isSuperAdmin ? setTraceTurn : undefined}
        banner={(actionError || chat.error) ? <ErrorLine>{actionError || chat.error}</ErrorLine> : undefined}
      />
    );
  }

  return (
    <Frame>
      <aside className="cpt-sb-fan cpt-thin-scroll" style={{ width: 300, flex: '0 1 300px', minWidth: 240, borderRight: `1px solid ${C.divider}`, padding: 16, display: 'flex', flexDirection: 'column', gap: 16, overflowY: 'auto', boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <span style={{ font: f(700, 17, 1.2), color: C.text }}>{t('sb.title')}</span>
          <span style={{ font: f(400, 12, 1.4), color: C.sub }}>{t('sb.desc')}</span>
        </div>
        {fanId && meta ? (
          <>
            <Block title={t('sb.testFan')}>
              <Line label={t('sb.name')}>
                <span title={profile.username ? `@${profile.username}` : undefined} style={{ font: f(500, 12.5), color: C.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{fanName}</span>
              </Line>
              <Line label={t('sb.spent')}>
                <span style={{ font: f(600, 12.5), color: C.text, fontVariantNumeric: 'tabular-nums' }}>{money(spentOf(messages))}</span>
              </Line>
              <ListsLine key={fanId} lists={meta.lists.map((l) => l.name)} all={ofLists} onSave={saveLists} onRefresh={refreshLists} />
            </Block>
            <Block title={t('sb.where')}>
              <Line label={t('sb.ai')}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 6, font: f(500, 12.5), color: aiState === 'idle' || aiState === 'unanswered' ? C.text : C.purpleLt, whiteSpace: 'nowrap' }}>
                  {aiState !== 'idle' && aiState !== 'unanswered' && <span className="cpt-sb-dot" style={{ width: 6, height: 6 }} />}
                  {t(`sb.aiState.${aiState}`)}
                </span>
              </Line>
              <Line label={t('sb.offers')}>
                <span style={{ font: f(500, 12.5), color: C.text, whiteSpace: 'nowrap' }}>{t('sb.offersVal', { sent: offers.sent, unlocked: offers.unlocked })}</span>
              </Line>
            </Block>
            <Block title={t('sb.playback')}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7, padding: '9px 12px 10px' }}>
                <span style={{ font: f(400, 12.5), color: C.sub }}>{t('sb.speed')}</span>
                <div style={{ alignSelf: 'flex-start' }}>
                  <Seg options={PACINGS.map((id) => ({ id, label: t(`sb.speedOpt.${id}`) }))} value={pacing} onChange={setPacing} />
                </div>
                <span style={{ font: f(400, 11.5, 1.4), color: C.muted }}>{t(`sb.speedHint.${pacing}`)}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '9px 12px' }}>
                <span title={t('sb.showEventsHint')} style={{ font: f(400, 12.5), color: C.sub }}>{t('sb.showEvents')}</span>
                <Toggle on={showSystem} onChange={setShowSystem} title={t('sb.showEventsHint')} />
              </div>
            </Block>
          </>
        ) : (
          <InfoLine>{t('sb.costNote')}</InfoLine>
        )}
      </aside>

      <div className="cpt-sb-chat" style={{ flex: 1, minWidth: 0, background: C.page, display: 'flex', justifyContent: 'center', padding: 16 }}>
        {center}
      </div>

      <aside className="cpt-sb-hist" style={{ width: 280, flex: '0 1 280px', minWidth: 220, borderLeft: `1px solid ${C.divider}`, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <div style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 8, padding: '14px 12px 10px 16px' }}>
          <span style={{ font: f(600, 13), color: C.text }}>{t('sb.history')}</span>
          <span style={{ font: f(500, 11.5), color: C.muted }}>{chats ? `${chats.length}/${MAX_OPEN_CHATS}` : ''}</span>
          <span style={{ flex: 1 }} />
          <Btn kind="primary" icon="plus" onClick={() => setCreating(true)} disabled={atLimit || !!(listError && !chats?.length)} title={atLimit ? t('sb.limitReached', { max: MAX_OPEN_CHATS }) : undefined}>{t('sb.newChat')}</Btn>
        </div>
        <div className="cpt-thin-scroll" style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '0 8px 12px', display: 'flex', flexDirection: 'column', gap: 2 }}>
          {[...(chats ?? [])].reverse().map((c) => {
            const on = c.fan_id === fanId;
            const mine = !!user && c.created_by === user.id;
            const preset = c.profile?.preset;
            return (
              <div
                key={c.fan_id}
                role="button"
                tabIndex={0}
                onClick={() => select(c.fan_id)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); select(c.fan_id); } }}
                className={on ? undefined : 'cpt-sb-hist'}
                style={{ display: 'flex', flexDirection: 'column', gap: 5, padding: 10, borderRadius: 8, background: on ? 'var(--om-divider)' : 'transparent', border: `1px solid ${on ? 'var(--om-border-strong)' : 'transparent'}`, cursor: 'pointer' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ flex: 1, minWidth: 0, font: f(500, 13), color: C.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.profile?.name || c.profile?.displayName || 'Test fan'}</span>
                  <span style={{ flex: 'none', font: f(400, 11), color: C.muted, whiteSpace: 'nowrap' }}>{whenLabel(c.created_at, t('sb.yesterday'))}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                  <span style={{ font: f(400, 11.5), color: C.sub, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {[preset ? t(`sb.presets.${preset}`, { defaultValue: preset }) : null, c.lists?.length ? c.lists.map((l) => l.name).join(', ') : null].filter(Boolean).join(' · ') || t('sb.noLists')}
                  </span>
                  <span style={{ flex: 1 }} />
                  {mine && <span style={{ height: 20, padding: '0 7px', display: 'flex', alignItems: 'center', borderRadius: 99, background: C.tint20, color: C.purplePale, font: f(500, 11), whiteSpace: 'nowrap' }}>{t('sb.you')}</span>}
                </div>
              </div>
            );
          })}
          {listError && chats && chats.length > 0 && <ErrorLine>{listError}</ErrorLine>}
        </div>
      </aside>

      {traceTurn && fanId && <TraceDrawer accountId={accountId} fanId={fanId} turnId={traceTurn} onClose={() => setTraceTurn(null)} />}
      {creating && <NewChatDialog accountId={accountId} lists={ofLists} onRefreshLists={refreshLists} onClose={() => setCreating(false)} onCreated={(id) => void created(id)} />}
    </Frame>
  );
}

function Frame({ children }: { children: ReactNode }) {
  return (
    <div className="cpt-root cpt-sb-layout" style={{ display: 'flex', height: 'calc(100 * var(--om-vh) - 200px)', minHeight: 620, background: C.surface, border: `1px solid ${C.borderLo}`, borderRadius: 10, overflow: 'hidden' }}>
      {children}
    </div>
  );
}

function Centered({ icon, title, body, action }: { icon: string; title: string; body: string; action?: ReactNode }) {
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, padding: 24, textAlign: 'center' }}>
      <span style={{ width: 44, height: 44, borderRadius: 99, background: C.tint16, color: C.purpleLt, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon name={icon} size={20} /></span>
      <span style={{ font: f(600, 15), color: C.text }}>{title}</span>
      <span style={{ maxWidth: 380, font: f(400, 12.5, 1.5), color: C.sub }}>{body}</span>
      {action}
    </div>
  );
}

function Block({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <span style={{ font: f(600, 13), color: C.text }}>{title}</span>
      <div className="cpt-card" style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8 }}>{children}</div>
    </div>
  );
}

function Line({ label, title, children }: { label: string; title?: string; children: ReactNode }) {
  return (
    <div title={title} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, minHeight: 34, padding: '0 12px', minWidth: 0 }}>
      <span style={{ font: f(400, 12.5), color: C.sub, flex: 'none' }}>{label}</span>
      {children}
    </div>
  );
}

function ListsLine({ lists, all, onSave, onRefresh }: { lists: string[]; all: string[]; onSave: (names: string[]) => Promise<boolean>; onRefresh: () => Promise<unknown> }) {
  const { t } = useTranslation('assistantCompact');
  const [editing, setEditing] = useState<string[] | null>(null);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!editing) return;
    setSaving(true);
    if (await onSave(editing)) setEditing(null);
    setSaving(false);
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '9px 12px 10px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ font: f(400, 12.5), color: C.sub }}>{t('sb.lists')}</span>
        {editing ? (
          <span style={{ display: 'flex', gap: 4 }}>
            <Btn kind="text" height={22} onClick={() => setEditing(null)} disabled={saving}>{t('cancel')}</Btn>
            <Btn kind="tint" height={22} onClick={() => void save()} disabled={saving}>{t('sb.save')}</Btn>
          </span>
        ) : (
          <Btn kind="text" height={22} icon="edit-2" onClick={() => setEditing(lists)}>{t('sb.edit')}</Btn>
        )}
      </div>
      {editing ? (
        <ListPicker all={all} value={editing} onChange={setEditing} onRefresh={onRefresh} maxHeight={180} />
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {lists.length ? lists.map((l) => (
            <span key={l} style={{ height: 22, padding: '0 8px', display: 'flex', alignItems: 'center', background: C.input, border: `1px solid ${C.borderHi}`, borderRadius: 6, font: f(500, 11.5), color: 'var(--om-body)', whiteSpace: 'nowrap' }}>{l}</span>
          )) : <span style={{ font: f(400, 12), color: C.muted }}>{t('sb.noLists')}</span>}
        </div>
      )}
    </div>
  );
}
